import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  MEETING_IMPORT_CONTRACT as bounds,
  validateMeetingReceipt,
  meetingReviewDraft,
  meetingReviewItem,
  type MeetingReviewDraft,
  type MeetingPeopleMatches,
  type MeetingCommitItem,
  type MeetingDestination,
  type MeetingImport,
  type MeetingLocalEntry,
  type MeetingSource,
} from '@cerebro-diem/core';
import { useAuthStore } from '../../stores/authStore';
import { useContainers, containerPath } from '../../services/containers';
import { refreshFilingViews } from '../../services/queries';
import { errorMessage } from '../../services/errors';
import {
  meetingClient,
  prepareMeetingFile,
  findMeetingPeople,
} from './meetingService';
const sourceName = (source: MeetingSource) =>
  source.kind === 'url'
    ? source.url
    : source.kind === 'media'
      ? source.name
      : (source.name ?? 'Pasted transcript');
const field =
  'w-full min-w-0 rounded-lg border border-outline/40 bg-surface px-3 py-2 text-base disabled:opacity-60';
const action =
  'rounded-lg bg-primary text-on-primary px-4 py-3 disabled:opacity-50';
export default function MeetingImportPanel() {
  const ownerId = useAuthStore((s) => s.user?.id);
  return ownerId ? (
    <MeetingImportFlow key={ownerId} ownerId={ownerId} />
  ) : (
    <p>Sign in to import a meeting.</p>
  );
}
function MeetingImportFlow({ ownerId }: { ownerId: string }) {
  const queryClient = useQueryClient(),
    containersQuery = useContainers();
  const [mode, setMode] = useState<'url' | 'text' | 'file'>('url'),
    [url, setUrl] = useState(''),
    [text, setText] = useState(''),
    [file, setFile] = useState<File | null>(null);
  const [imports, setImports] = useState<MeetingImport[]>([]),
    [pending, setPending] = useState<MeetingLocalEntry[]>([]);
  const [row, setRow] = useState<MeetingImport | null>(null),
    [drafts, setDrafts] = useState<MeetingReviewDraft[]>([]),
    [activeId, setActiveId] = useState<string | null>(null),
    [source, setSource] = useState<MeetingSource | null>(null);
  const [target, setTarget] = useState('new'),
    [newArea, setNewArea] = useState(''),
    [busy, setBusy] = useState(false),
    [locked, setLocked] = useState(false),
    [dirty, setDirty] = useState(false),
    [error, setError] = useState<string | null>(null),
    [notice, setNotice] = useState('');
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    void Promise.allSettled([
      meetingClient.listMeetingImports(ownerId),
      meetingClient.pendingMeetingImports(ownerId),
    ]).then((results) => {
      if (!alive.current) return;
      const [remote, local] = results;
      const errors: string[] = [];
      if (remote.status === 'fulfilled') setImports(remote.value);
      else
        errors.push(
          `Meeting history could not be loaded: ${errorMessage(remote.reason)}`,
        );
      if (local.status === 'fulfilled') setPending(local.value);
      else errors.push(errorMessage(local.reason));
      if (errors.length) setError(errors.join('. '));
    });
  }, [ownerId]);
  // Raw text is the editable source; canonical arrays derive from it for lookup/save.
  const items = drafts.map(meetingReviewItem);
  const setItems = (next: MeetingCommitItem[]) =>
    setDrafts(next.map(meetingReviewDraft));
  const namesKey = JSON.stringify([
    ...new Set(items.flatMap((item) => item.people)),
  ]);
  const [peopleRevision, setPeopleRevision] = useState(0);
  const lookupKey = `${peopleRevision}:${namesKey}`;
  const [peopleLookup, setPeopleLookup] = useState<{
    key: string;
    rows: MeetingPeopleMatches[];
    error?: string;
  } | null>(null);
  useEffect(() => {
    let current = true;
    const names: string[] = JSON.parse(namesKey);
    void (
      names.length ? findMeetingPeople(ownerId, names) : Promise.resolve([])
    ).then(
      (rows) => {
        if (current && alive.current) setPeopleLookup({ key: lookupKey, rows });
      },
      (reason) => {
        if (current && alive.current)
          setPeopleLookup({
            key: lookupKey,
            rows: [],
            error: errorMessage(reason),
          });
      },
    );
    return () => {
      current = false;
    };
  }, [ownerId, namesKey, lookupKey]);
  const peopleLoading = peopleLookup?.key !== lookupKey;
  const peopleReady = !peopleLoading && !peopleLookup?.error;
  const namesFor = (name: string) =>
    peopleReady
      ? (peopleLookup?.rows.find((row) => row.name === name)?.matches ?? [])
      : [];
  const containers = (containersQuery.data ?? []).filter(
    (c) =>
      c.user_id === ownerId &&
      !c.archived_at &&
      (c.kind === 'area' || c.kind === 'project'),
  );
  const entries = [
    ...imports.map((r) => ({ id: r.id, source: r.source, status: r.status })),
    ...pending
      .filter((e) => !imports.some((r) => r.id === e.id))
      .map((e) => ({
        id: e.id,
        source: e.source,
        status: e.commit
          ? 'reconcile'
          : e.uploadPending
            ? 'upload pending'
            : 'create pending',
      })),
  ];
  const ambiguous = items.some(
    (item) =>
      item.selected &&
      item.people.some((name) => {
        const matches = namesFor(name);
        return (
          matches.length > 1 &&
          matches.filter((p) => item.personIds?.includes(p.id)).length !== 1
        );
      }),
  );
  const destination = (): MeetingDestination => {
    if (target === 'new') return { kind: 'area', newName: newArea };
    const container = containers.find((c) => `${c.kind}:${c.id}` === target);
    if (!container) throw new Error('Choose a Project or Area');
    return { kind: container.kind, id: container.id };
  };
  const accept = (saved: MeetingImport) => {
    setRow(saved);
    setItems(saved.candidates);
    setActiveId(saved.id);
    setSource(saved.source);
    setDirty(false);
  };
  const refreshPending = async () => {
    const local = await meetingClient.pendingMeetingImports(ownerId);
    if (alive.current) setPending(local);
    return local;
  };
  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice('');
    try {
      await work();
    } catch (reason) {
      if (alive.current) setError(errorMessage(reason));
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  const progress = (saved: MeetingImport) => {
    if (alive.current) {
      setRow(saved);
      setSource(saved.source);
      setNotice(
        saved.status === 'pending'
          ? 'Acquiring retained source…'
          : `Extracting meeting items · ${saved.next_chunk} chunks completed`,
      );
    }
  };
  async function resume(id: string) {
    await run(async () => {
      setActiveId(id);
      const local = (await refreshPending()).find((e) => e.id === id);
      if (local?.commit) {
        const saved = await meetingClient.getMeetingImport(ownerId, id);
        if (!alive.current) return;
        accept(saved);
        setItems(local.commit.items);
        setLocked(true);
        setTarget(
          local.commit.container.id
            ? `${local.commit.container.kind}:${local.commit.container.id}`
            : 'new',
        );
        setNewArea(local.commit.container.newName ?? '');
        return;
      }
      const saved = await meetingClient.processMeetingImport(
        ownerId,
        id,
        progress,
      );
      if (!alive.current) return;
      accept(saved);
      setLocked(false);
      setNotice(
        saved.candidates.length
          ? 'Review every item before saving.'
          : 'No actionable items found. The original source is retained.',
      );
      await refreshPending();
    });
  }
  async function start() {
    await run(async () => {
      const id = crypto.randomUUID();
      const original: MeetingSource =
        mode === 'url'
          ? { kind: 'url', url }
          : mode === 'text'
            ? { kind: 'text', text }
            : file
              ? await prepareMeetingFile(ownerId, id, file)
              : (() => {
                  throw new Error('Choose a meeting file');
                })();
      if (!alive.current) return;
      setActiveId(id);
      setSource(original);
      setRow(null);
      setItems([]);
      setLocked(false);
      await meetingClient.createMeetingImport(ownerId, id, original);
      await refreshPending();
      const saved = await meetingClient.processMeetingImport(
        ownerId,
        id,
        progress,
      );
      if (!alive.current) return;
      accept(saved);
      setLocked(false);
      setNotice(
        saved.candidates.length
          ? 'Review every item before saving.'
          : 'No actionable items found. The original source is retained.',
      );
    });
  }
  function edit(index: number, patch: Partial<MeetingReviewDraft>) {
    setDrafts((current) =>
      current.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
    setDirty(true);
    setNotice('');
  }
  async function saveReview() {
    if (!activeId) return;
    await run(async () => {
      const saved = await meetingClient.updateMeetingReview(
        ownerId,
        activeId,
        items,
      );
      if (alive.current) {
        accept(saved);
        setNotice('Review saved. Edits will be here after reopening.');
      }
    });
  }
  async function commit() {
    if (!activeId) return;
    await run(async () => {
      let submissionItems = items,
        container: MeetingDestination;
      if (locked) {
        const saved = (await refreshPending()).find(
          (e) => e.id === activeId,
        )?.commit;
        if (!saved)
          throw new Error(
            'Saved submission is unavailable; reload to reconcile',
          );
        submissionItems = saved.items;
        container = saved.container;
      } else {
        container = destination();
        const reviewed = await meetingClient.updateMeetingReview(
          ownerId,
          activeId,
          items,
        );
        if (!alive.current) return;
        accept(reviewed);
        submissionItems = reviewed.candidates;
        setLocked(true);
      }
      try {
        const result = await meetingClient.commitMeetingImport(
          ownerId,
          activeId,
          submissionItems,
          container,
        );
        if (!alive.current) return;
        accept(result.import);
        setLocked(false);
        setNotice(
          `Saved ${result.receipt.items.length} meeting item${result.receipt.items.length === 1 ? '' : 's'}.`,
        );
        setImports((current) => current.filter((r) => r.id !== activeId));
        await refreshPending();
        if (alive.current) await refreshFilingViews(queryClient);
      } catch (reason) {
        if (alive.current) {
          const local = await refreshPending();
          const retained = Boolean(
            local.find((e) => e.id === activeId)?.commit,
          );
          setLocked(retained);
          if (!retained) {
            setDrafts((current) =>
              current.map((item) => ({ ...item, personIds: [] })),
            );
            setDirty(true);
            setPeopleRevision((value) => value + 1);
          }
        }
        throw reason;
      }
    });
  }
  const receipt =
    row?.status === 'filed' && !locked
      ? validateMeetingReceipt(row.receipt, row.id)
      : null;
  return (
    <section aria-label="Meeting import" className="space-y-4 min-w-0">
      <h2 className="text-xl font-semibold">Import meeting</h2>
      <p>
        Keep the complete meeting source, then review and save each task or
        note.
      </p>
      <p className="text-sm">
        TXT, VTT and SRT: up to{' '}
        {bounds.max_transcript_chars.toLocaleString('en-US')} characters (
        {(bounds.max_transcript_chars * 4) / 1_000_000} MB file). Recordings:{' '}
        {bounds.media_extensions.join(', ')} · up to{' '}
        {bounds.max_media_bytes / 1_000_000} MB.
      </p>
      <div className="flex flex-wrap gap-2">
        {(
          [
            ['url', 'Meeting link'],
            ['text', 'Transcript'],
            ['file', 'Meeting file'],
          ] as const
        ).map(([key, label]) => (
          <button
            type="button"
            key={key}
            className={action}
            aria-pressed={mode === key}
            disabled={busy}
            onClick={() => setMode(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {mode === 'url' ? (
        <label className="block">
          Meeting URL
          <input
            aria-label="Meeting URL"
            type="url"
            className={field}
            value={url}
            disabled={busy}
            onChange={(e) => setUrl(e.target.value)}
          />
        </label>
      ) : mode === 'text' ? (
        <label className="block">
          Meeting transcript
          <textarea
            aria-label="Meeting transcript"
            rows={7}
            className={field}
            value={text}
            disabled={busy}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
      ) : (
        <label className="block">
          Choose transcript or recording
          <input
            aria-label="Meeting file"
            type="file"
            className={field}
            accept={`.txt,.vtt,.srt,${bounds.media_extensions.map((ext) => `.${ext}`).join(',')}`}
            disabled={busy}
            onChange={(e) => {
              const selected = e.target.files?.[0] ?? null;
              setFile(selected);
              setError(null);
            }}
          />
          {file ? (
            <span>
              {file.name} · {file.size.toLocaleString()} bytes
            </span>
          ) : null}
        </label>
      )}
      <button
        type="button"
        className={action}
        disabled={
          busy ||
          locked ||
          (mode === 'url'
            ? !url.trim()
            : mode === 'text'
              ? !text.trim()
              : !file)
        }
        onClick={() => void start()}
      >
        Import meeting
      </button>
      {error ? (
        <p role="alert" className="text-error whitespace-pre-wrap break-words">
          {error}
        </p>
      ) : null}
      {containersQuery.error ? (
        <p role="alert">
          Destinations could not be loaded:{' '}
          {errorMessage(containersQuery.error)}
        </p>
      ) : null}
      {notice ? <p role="status">{notice}</p> : null}
      {busy ? (
        <p role="status">
          Processing… Keep this page open. Progress and the original source are
          retained.
        </p>
      ) : null}
      {source ? (
        <p className="break-words">Original source: {sourceName(source)}</p>
      ) : null}
      {activeId &&
      !busy &&
      !locked &&
      row?.status !== 'review' &&
      row?.status !== 'filed' ? (
        <button
          type="button"
          className={action}
          onClick={() => void resume(activeId)}
        >
          Resume import
        </button>
      ) : null}
      {entries.length ? (
        <div className="space-y-2">
          <h3 className="font-semibold">Saved imports</h3>
          {entries.map((entry) => (
            <div key={entry.id} className="break-words">
              <span>
                {sourceName(entry.source)} · {entry.status}{' '}
              </span>
              <button
                type="button"
                className="underline min-h-11"
                disabled={busy}
                onClick={() => void resume(entry.id)}
              >
                {pending.find((p) => p.id === entry.id)?.commit
                  ? 'Reconcile import'
                  : 'Open import'}
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {row?.status === 'review' && items.length ? (
        <>
          <h3 className="font-semibold">
            Review {items.length} extracted items
          </h3>
          {dirty ? (
            <p role="status">
              Unsaved review changes. Save review to keep edits across restart.
            </p>
          ) : null}
          {locked ? (
            <p role="status">
              Filing outcome is uncertain. Reconcile the saved submission;
              editing stays disabled until the receipt is verified.
            </p>
          ) : null}
          <fieldset disabled={busy || locked} className="space-y-4 min-w-0">
            {items.map((item, index) => (
              <article
                key={item.id}
                className="border border-outline/30 rounded-xl p-3 space-y-3 min-w-0"
              >
                <label className="flex items-center gap-2 min-h-11">
                  <input
                    type="checkbox"
                    aria-label={`Include item ${index + 1}`}
                    checked={item.selected}
                    onChange={(e) =>
                      edit(index, { selected: e.target.checked })
                    }
                  />
                  Include {item.kind} {index + 1}
                </label>
                <label className="block">
                  Title
                  <input
                    aria-label={`Title ${index + 1}`}
                    className={field}
                    value={item.title}
                    onChange={(e) => edit(index, { title: e.target.value })}
                  />
                </label>
                <label className="block">
                  Body
                  <textarea
                    aria-label={`Body ${index + 1}`}
                    className={field}
                    value={item.body}
                    onChange={(e) => edit(index, { body: e.target.value })}
                  />
                </label>
                <label className="block">
                  People (comma separated)
                  <input
                    aria-label={`People ${index + 1}`}
                    className={field}
                    value={drafts[index].peopleText}
                    onChange={(e) =>
                      edit(index, {
                        peopleText: e.target.value,
                        personIds: [],
                      })
                    }
                  />
                </label>
                {item.people.map((name) => {
                  const matches = namesFor(name);
                  return matches.length > 1 ? (
                    <label className="block" key={name}>
                      Choose which {name}
                      <select
                        aria-label={`Choose ${name} for item ${index + 1}`}
                        className={field}
                        value={
                          matches.find((p) => item.personIds?.includes(p.id))
                            ?.id ?? ''
                        }
                        onChange={(e) =>
                          edit(index, {
                            personIds: [
                              ...(item.personIds ?? []).filter(
                                (id) => !matches.some((p) => p.id === id),
                              ),
                              ...(e.target.value ? [e.target.value] : []),
                            ],
                          })
                        }
                      >
                        <option value="">Choose a person</option>
                        {matches.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} · {p.context ?? 'No context'} · added{' '}
                            {p.created_at.slice(0, 10)}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null;
                })}
                <label className="block">
                  Due date
                  <input
                    aria-label={`Due date ${index + 1}`}
                    className={field}
                    type="date"
                    value={item.dueDate ?? ''}
                    onChange={(e) =>
                      edit(index, { dueDate: e.target.value || null })
                    }
                  />
                </label>
                <p className="text-sm font-medium">Source excerpt</p>
                <blockquote className="whitespace-pre-wrap break-words border-l-2 border-primary pl-3">
                  {item.quote}
                </blockquote>
              </article>
            ))}
            <p>
              Missing People names are created when saved. Duplicate names
              require an explicit choice.
            </p>
            {peopleLoading && (
              <p role="status">Checking complete People matches…</p>
            )}
            {peopleLookup?.error && (
              <p role="alert">People lookup failed: {peopleLookup.error}</p>
            )}
            <button
              type="button"
              disabled={busy || locked}
              onClick={() => setPeopleRevision((value) => value + 1)}
            >
              Refresh People matches
            </button>
            <label className="block">
              Destination
              <select
                aria-label="Meeting destination"
                className={field}
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                <option value="new">New Area</option>
                {containers.map((c) => (
                  <option key={`${c.kind}:${c.id}`} value={`${c.kind}:${c.id}`}>
                    {c.kind === 'project' ? 'Project' : 'Area'}: {c.name}
                  </option>
                ))}
              </select>
            </label>
            {target === 'new' ? (
              <label className="block">
                New Area name
                <input
                  aria-label="New Area name"
                  className={field}
                  value={newArea}
                  onChange={(e) => setNewArea(e.target.value)}
                />
              </label>
            ) : null}
            <button
              type="button"
              className={action}
              disabled={!dirty || !items.some((i) => i.selected)}
              onClick={() => void saveReview()}
            >
              Save review
            </button>
          </fieldset>
          {ambiguous && !locked ? (
            <p role="alert">
              Choose a matching person for each duplicate name before filing.
            </p>
          ) : null}
          <button
            type="button"
            className={action}
            disabled={
              busy ||
              (!locked &&
                (!peopleReady ||
                  ambiguous ||
                  !items.some((i) => i.selected) ||
                  (target === 'new' && !newArea.trim())))
            }
            onClick={() => void commit()}
          >
            {locked ? 'Reconcile saved submission' : 'Save selected items'}
          </button>
        </>
      ) : null}
      {locked && row?.status !== 'review' ? (
        <div role="status">
          <p>Verify the receipt for the retained submission before editing.</p>
          <button
            type="button"
            className={action}
            disabled={busy}
            onClick={() => void commit()}
          >
            Reconcile saved submission
          </button>
        </div>
      ) : null}
      {receipt ? (
        <div role="status">
          <p>
            Saved {receipt.items.length} meeting item
            {receipt.items.length === 1 ? '' : 's'}.
          </p>
          <p>Receipt: {receipt.import_id}</p>
          {receipt.items.map((item) => (
            <p key={item.candidate_id}>
              <Link
                className="underline"
                to={containerPath(item.container_kind, item.container_id)}
              >
                Open {item.container_kind}
              </Link>{' '}
              ·{' '}
              <Link
                className="underline"
                to={`/${item.kind === 'task' ? 'tasks' : 'notes'}/${item.destination_id}`}
              >
                Open {item.kind}
              </Link>
            </p>
          ))}
        </div>
      ) : null}
    </section>
  );
}
